// Core search logic and flow

import { getAuthHeaders, getSubscriptionStatusUrl, authenticatedFetch } from '../api/auth.js';
import {
    clearActiveResearchIdOnServer,
    getCurrentResearchId,
    setCurrentResearchId
} from '../state/research.js';
import {
    getOrCreateSessionId,
    isFirstQueryInSession,
    markSearchPerformed
} from '../state/session.js';
import {
    resetTools,
    showTools
} from './toolbar.js';
import {
    moveSearchBoxToBottom,
    moveHeaderBoxToTop
} from './positioning.js';
import {
    enableEditMode
} from './edit-mode.js';
import { capture as posthogCapture } from '../analytics/posthog.js';
import {
    updateAssistantMessage,
    getResearchPersistedOutputHtml,
    BMS_CANONICAL_INNER_HTML_KEY
} from './messages.js';
import {
    displayCitations,
    fetchAndDisplayCitations,
    createCitationFormatDropdown
} from './citations.js';
import {
    fetchSuggestions
} from './suggestions.js';
import {
    createCompareButton
} from './compare-button.js';
import {
    goToPubMed
} from './actions.js';
import {
    waitForNewSearchButtonAndSave
} from './research-session-loader.js';

/** Format seconds until expiry as "Xh Ym", "Xm", or "< 1m". */
function formatResetsIn(expiresAtEpochSeconds) {
    if (expiresAtEpochSeconds == null) return '';
    const now = Math.floor(Date.now() / 1000);
    let sec = Math.max(0, expiresAtEpochSeconds - now);
    if (sec < 60) return '< 1m';
    const mins = Math.floor(sec / 60);
    const hrs = Math.floor(mins / 60);
    if (hrs >= 1) {
        const m = mins % 60;
        return m > 0 ? `${hrs}h ${m}m` : `${hrs}h`;
    }
    return `${mins}m`;
}

/** Show overlay when search limit is reached. limitType: 'free' | 'pro_fair_use'. */
function showSearchLimitOverlay(limitType = 'free') {
    let overlay = document.getElementById('searchLimitOverlay');

    if (!overlay) {
        overlay = document.createElement('div');
        overlay.id = 'searchLimitOverlay';
        overlay.className = 'search-limit-overlay';
        overlay.innerHTML = `
            <div class="search-limit-container">
                <p class="search-limit-subtext"></p>
                <div class="search-limit-button-wrap"></div>
            </div>
        `;
        document.body.appendChild(overlay);

        overlay.addEventListener('click', (e) => {
            if (e.target === overlay) {
                overlay.classList.add('hidden');
                document.body.style.overflow = '';
            }
        });
        const escapeHandler = function (e) {
            if (e.key === 'Escape' && !overlay.classList.contains('hidden')) {
                overlay.classList.add('hidden');
                document.body.style.overflow = '';
                document.removeEventListener('keydown', escapeHandler);
            }
        };
        document.addEventListener('keydown', escapeHandler);
    }

    const subtext = overlay.querySelector('.search-limit-subtext');
    const btnWrap = overlay.querySelector('.search-limit-button-wrap');
    if (limitType === 'pro_fair_use') {
        if (subtext) subtext.textContent = "You've reached today's fair use limit. Please try again tomorrow.";
        if (btnWrap) btnWrap.innerHTML = '';
    } else {
        if (subtext) subtext.innerHTML = "You've reached the FREE limit of 10 searches today.<br>For unlimited searches:";
        if (btnWrap) btnWrap.innerHTML = '<a href="/pricing" class="search-limit-button">Upgrade to Pro</a>';
    }

    overlay.classList.remove('hidden');
    document.body.style.overflow = 'hidden';
}

/** Apply search limit reached: message above search box + disable UI; optionally show overlay.
 * @param {Object} [opts] - options.showOverlay: if false, do not show overlay (e.g. on initial page load).
 */
function applySearchLimitReached(isProFairUseLimit, data, opts = {}) {
    posthogCapture('search_limit_reached', {
        limit_type: isProFairUseLimit ? 'pro_fair_use' : 'free',
        expires_at: data && data.expires_at ? data.expires_at : undefined
    });
    updateFairUseThresholdMessage(false);
    const msgEl = document.getElementById('freePlanRemainingMsg');
    if (msgEl) {
        msgEl.classList.remove('hidden');
        if (isProFairUseLimit) {
            proFairUseLimitReached = true;
            msgEl.textContent = "You've reached today's fair use limit. Please try again tomorrow.";
        } else {
            const resetsIn = formatResetsIn(data && data.expires_at ? data.expires_at : null);
            msgEl.innerHTML = resetsIn
                ? `Free limit reached – Resets in ${resetsIn} – <a href="/pricing">Upgrade to Pro</a>`
                : `Free limit reached – <a href="/pricing">Upgrade to Pro</a>`;
        }
    }
    disableSearchUI();
    if (opts.showOverlay !== false) {
        showSearchLimitOverlay(isProFairUseLimit ? 'pro_fair_use' : 'free');
    }
}

const FREE_SEARCH_LIMIT = 10;

let searchInProgress = false;
let proFairUseLimitReached = false;

function disableSearchUI() {
    const searchInput = document.getElementById('searchInput');
    const searchBtn = document.getElementById('searchBtn');
    const searchBtns = document.querySelectorAll('.searchBtn');
    if (searchInput) {
        searchInput.disabled = true;
        searchInput.setAttribute('aria-busy', 'true');
    }
    searchBtns.forEach(btn => {
        btn.disabled = true;
        btn.setAttribute('aria-busy', 'true');
    });
}

function enableSearchUI() {
    const doEnable = () => {
        if (searchInProgress) return;
        if (proFairUseLimitReached) return;
        const searchInput = document.getElementById('searchInput');
        const searchBtns = document.querySelectorAll('.searchBtn');
        if (searchInput) {
            searchInput.disabled = false;
            searchInput.removeAttribute('aria-busy');
        }
        searchBtns.forEach(btn => {
            btn.disabled = false;
            btn.removeAttribute('aria-busy');
        });
    };
    // Defer to next frame so layout has settled (fixes small screen where enable was not taking effect)
    requestAnimationFrame(doEnable);
}

/** Check if user can perform a search (not at limit). Returns true if allowed, false if limit reached.
 * When limit reached, disables UI and shows message; shows overlay unless options.fromInit is true (e.g. on page load).
 * @param {{ fromInit?: boolean }} [options] - pass { fromInit: true } when called on initial page load to skip showing the overlay.
 */
export async function checkSearchLimit(options = {}) {
    const isLoggedIn = localStorage.getItem('access_token') !== null;
    let isPro = false;

    // Run both limit-check requests concurrently instead of sequentially (same fix as handleSearch).
    const [subscriptionResult, searchCountResult] = await Promise.all([
        isLoggedIn
            ? authenticatedFetch(getSubscriptionStatusUrl())
                  .then((r) => (r.ok ? r.json() : null))
                  .catch((error) => {
                      console.error('[Search Limit] Error checking subscription status:', error);
                      return null;
                  })
            : Promise.resolve(null),
        authenticatedFetch('/anonymous-search-count')
            .then((r) => r.json())
            .catch((error) => {
                console.error('[Search Limit] Error checking search limit:', error);
                return null;
            }),
    ]);

    if (subscriptionResult && subscriptionResult.status === 'success') {
        isPro = subscriptionResult.is_pro;
    }

    if (!searchCountResult) {
        updateFairUseThresholdMessage(false);
        return true; // Allow on error (fallback)
    }

    const data = searchCountResult;
    const limitReached = (!isPro && data.search_count >= FREE_SEARCH_LIMIT) || (isPro && data.fair_use_limit_reached);
    if (limitReached) {
        applySearchLimitReached(!!(isPro && data.fair_use_limit_reached), data, { showOverlay: !options.fromInit });
        return false;
    }
    if (isPro) {
        updateFreePlanRemainingMessage(null);
        updateFairUseThresholdMessage(!!data.fair_use_throttle, false);
    } else {
        updateFairUseThresholdMessage(false);
        updateFreePlanRemainingMessage(data.search_count, data.expires_at);
    }
    return true;
}

/** Show or hide the Pro fair-use message above the searchbox.
 * @param {boolean} show - show the message
 * @param {boolean} limitReached - if true, show "fair use limit" message and disable search; if false and show, show "high activity" message
 */
export function updateFairUseThresholdMessage(show, limitReached = false) {
    proFairUseLimitReached = !!(show && limitReached);
    const el = document.getElementById('fairUseThresholdMsg');
    if (el) {
        if (show && limitReached) {
            el.classList.remove('hidden');
            el.textContent = "You've reached today's fair use limit. Please try again tomorrow.";
            disableSearchUI();
        } else if (show) {
            el.classList.remove('hidden');
            el.innerHTML = '<span class="material-symbols-rounded fair-use-threshold-icon notranslate">bolt</span> High activity today – responses may be slightly slower';
            if (!searchInProgress) enableSearchUI();
        } else {
            el.classList.add('hidden');
            el.textContent = '';
            if (!searchInProgress) enableSearchUI();
        }
    }
}

/** Update or hide the free plan message. Call with count (number) or null to hide; expiresAt (epoch sec) optional; allowEnable=false skips re-enabling (e.g. when Pro fair-use limit reached). */
export function updateFreePlanRemainingMessage(count, expiresAtEpochSeconds, allowEnable = true) {
    const el = document.getElementById('freePlanRemainingMsg');
    if (el) {
        if (count == null || count < 7) {
            el.classList.add('hidden');
            el.classList.remove('free-plan-remaining-msg--low');
            el.textContent = '';
            el.innerHTML = '';
        } else {
            el.classList.remove('hidden');
            if (count >= FREE_SEARCH_LIMIT) {
                el.classList.add('free-plan-remaining-msg--low');
                const resetsIn = formatResetsIn(expiresAtEpochSeconds);
                el.innerHTML = resetsIn
                    ? `Free limit reached – Resets in ${resetsIn} – <a href="/pricing">Upgrade to Pro</a>`
                    : `Free limit reached – <a href="/pricing">Upgrade to Pro</a>`;
            } else {
                const remaining = FREE_SEARCH_LIMIT - count;
                el.textContent = `Free plan : ${remaining} search${remaining === 1 ? '' : 'es'} remaining today`;
                if (remaining <= 3) {
                    el.classList.add('free-plan-remaining-msg--low');
                } else {
                    el.classList.remove('free-plan-remaining-msg--low');
                }
            }
        }
    }
    if (count != null && count >= FREE_SEARCH_LIMIT) {
        disableSearchUI();
    } else if (allowEnable) {
        if (!searchInProgress) {
            enableSearchUI();
        }
    }
}

// Show upgrade overlay when research limit is reached
function showResearchLimitOverlay() {
    // Check if overlay already exists
    let overlay = document.getElementById('researchLimitOverlay');
    
    if (!overlay) {
        // Create overlay
        overlay = document.createElement('div');
        overlay.id = 'researchLimitOverlay';
        overlay.className = 'research-limit-overlay';
        
        const upgradeHTML = `
            <div class="research-limit-container">
                <p class="research-limit-subtext">You've reached the free limit of 5 research topics.<br>Delete one from your saved research history, or for unlimited research:</p>
                <a href="/pricing" class="research-limit-button">Upgrade to Pro</a>
            </div>
        `;
        
        overlay.innerHTML = upgradeHTML;
        
        document.body.appendChild(overlay);
        
        // Close on overlay click
        overlay.addEventListener('click', (e) => {
            if (e.target === overlay) {
                overlay.classList.add('hidden');
                document.body.style.overflow = '';
            }
        });
        
        // Close on Escape key
        const escapeHandler = function(e) {
            if (e.key === 'Escape' && !overlay.classList.contains('hidden')) {
                overlay.classList.add('hidden');
                document.body.style.overflow = '';
                document.removeEventListener('keydown', escapeHandler);
            }
        };
        document.addEventListener('keydown', escapeHandler);
    }
    
    // Show overlay
    overlay.classList.remove('hidden');
    document.body.style.overflow = 'hidden';
}

export async function handleSearch(skipAnalysis = false) {
    console.log('[handleSearch] Function called, skipAnalysis:', skipAnalysis);

    // Prevent submitting another search while one is in progress
    if (searchInProgress) {
        console.log('[handleSearch] Search already in progress, ignoring');
        return;
    }

    const query = document.getElementById('searchInput').value;
    console.log('[handleSearch] Query:', query);

    // Check if input is empty before proceeding
    if (!query.trim()) {
        console.log('[handleSearch] Query is empty, returning');
        return;
    }
    
    const isLoggedIn = localStorage.getItem('access_token') !== null;
    console.log('[handleSearch] Is logged in:', isLoggedIn);
    
    // Check search count for non-Pro users (anonymous or authenticated)
    let isPro = false;

    // Run both limit-check requests concurrently instead of sequentially — they're
    // independent network calls and don't need to wait on each other.
    const [subscriptionResult, searchCountResult] = await Promise.all([
        isLoggedIn
            ? authenticatedFetch(getSubscriptionStatusUrl())
                  .then((r) => (r.ok ? r.json() : null))
                  .catch((error) => {
                      console.error('[Search Limit] Error checking subscription status:', error);
                      return null; // On error, assume not Pro (safer to apply limit)
                  })
            : Promise.resolve(null),
        authenticatedFetch('/anonymous-search-count')
            .then((r) => r.json())
            .catch((error) => {
                console.error('[Search Limit] Error checking search limit:', error);
                return null;
            }),
    ]);

    if (subscriptionResult && subscriptionResult.status === 'success') {
        isPro = subscriptionResult.is_pro;
    }

    if (searchCountResult) {
        const data = searchCountResult;
        console.log('[Search Limit] Search count check:', data);
        const limitReached = (!isPro && data.search_count >= FREE_SEARCH_LIMIT) || (isPro && data.fair_use_limit_reached);
        if (limitReached) {
            console.log('[Search Limit] Limit reached, showing overlay and disabling search');
            applySearchLimitReached(!!(isPro && data.fair_use_limit_reached), data);
            return;
        }
        if (isPro) {
            updateFreePlanRemainingMessage(null);
            updateFairUseThresholdMessage(!!data.fair_use_throttle, false);
        } else {
            updateFreePlanRemainingMessage(data.search_count, data.expires_at);
        }
    } else if (!isPro) {
        updateFairUseThresholdMessage(false);
    }

    // Lock search UI until response is done
    searchInProgress = true;
    disableSearchUI();

    let searchCountAfterSearch = null;
    let fairUseLimitHit = false;
    try {
    // Hide example strip and search button container after search limit check
    if (typeof window.bms?.hideExampleStrip === 'function') {
        window.bms.hideExampleStrip();
    } else if (typeof hideExampleStrip === 'function') {
        hideExampleStrip();
    }
    
    // Hide search button container
    const searchButtonContainer = document.querySelector('.search-button-container');
    if (searchButtonContainer) {
        searchButtonContainer.style.display = 'none';
    }
    
    // Note: research_id is now automatically created by the backend if user is authenticated
    // and no research_id is provided. We'll read it from response headers.
    
    // Hide all new-search-button-containers and suggested-chips-containers when a new search is initiated
    const containerElement = document.querySelector('.container');
    const existingNewSearchButtons = containerElement.querySelectorAll('.new-search-button-container');
    existingNewSearchButtons.forEach(btn => {
        btn.style.display = 'none';
    });
    // Hide all existing suggested-chips-containers (from previous responses)
    const existingSuggestedChips = containerElement.querySelectorAll('.suggested-chips-container');
    existingSuggestedChips.forEach(container => {
        container.style.display = 'none';
    });
    
    resetTools();
    const searchInput = document.getElementById('searchInput');
    const searchBox = document.getElementById('searchBox');
    
    // Move search box to bottom when user searches (do this first to set the class)
    moveSearchBoxToBottom();
    
    // Check if search box is now bottom-positioned (after moveSearchBoxToBottom)
    const isAlreadyBottomPositioned = searchBox && searchBox.classList.contains('bottom-positioned');
    
    searchInput.value = ''; // clear previous text
    searchInput.placeholder = "Follow-up search"; // Update placeholder immediately after clearing
    
    // Reset textarea height based on whether search box is already bottom-positioned
    // If already at bottom, keep it at single-line pill height (minimum), otherwise use 120px (initial)
    const defaultHeight = isAlreadyBottomPositioned ? '56px' : '120px';
    searchInput.style.setProperty('height', defaultHeight, 'important');
    searchInput.style.setProperty('overflow', 'hidden', 'important');
    moveHeaderBoxToTop();
    
    // Create and append new search result elements to container
    const container = document.querySelector('.container');
    const timestamp = Date.now(); // Generate unique timestamp for this search
    
    // Create a wrapper div for all search result elements (userQuery, response, toolbar, suggested chips)
    const searchResultWrapper = document.createElement('div');
    searchResultWrapper.id = `search-result-wrapper-${timestamp}`;
    searchResultWrapper.className = 'search-result-wrapper';
    
    // Create userQuery element with unique ID
    const userQueryDiv = document.createElement('div');
    userQueryDiv.id = `userQuery-${timestamp}`;
    userQueryDiv.className = 'userQuery-container';
    
    // Create actions container (direct edit-query button)
    const actions = document.createElement('div');
    actions.className = 'userQuery-actions';

    const actionsButton = document.createElement('button');
    actionsButton.type = 'button';
    actionsButton.className = 'userQuery-actions-btn';
    actionsButton.setAttribute('data-tooltip', 'Edit Query');
    actionsButton.innerHTML = '<span class="material-symbols-rounded notranslate">edit</span>';
    actionsButton.addEventListener('click', (event) => {
        event.stopPropagation();
        enableEditMode(userQueryDiv, timestamp);
    });

    actions.appendChild(actionsButton);

    // Create userQuery paragraph
    const userQueryP = document.createElement('p');
    userQueryP.className = 'userQuery';
    userQueryP.textContent = DOMPurify.sanitize(query).replace(/\n/g, '');
    
    // Append userQuery first, then actions (edit button on the right)
    userQueryDiv.appendChild(userQueryP);
    userQueryDiv.appendChild(actions);
    searchResultWrapper.appendChild(userQueryDiv);
    
    // Query-refinement state, populated by the analyze-query call below (after the spinner renders)
    let refinedQuery = query;
    const isFirstQuery = isFirstQueryInSession();
    let currentResearchId = getCurrentResearchId();
    // Capture sessionId once so /search receives it (backend needs it to save user_query_context for anonymous users)
    const sessionIdForSearch = getOrCreateSessionId();
    
    // Create response body wrapper with unique ID
    const responseBodyWrapper = document.createElement('div');
    responseBodyWrapper.id = `response-body-wrapper-${timestamp}`;
    responseBodyWrapper.className = 'response-body show';
    // Store timestamp for later retrieval (will be used when searchId is available)
    responseBodyWrapper.setAttribute('data-timestamp', timestamp);
    
    const output = document.createElement('div');
    output.id = `output-${timestamp}`;
    output.className = 'markdown-body';
    output.innerHTML = '<div id="loader"><div class="spinner"></div><span>Searching PubMed and Generating Cited Response...</span></div>';
    
    const referencesSection = document.createElement('div');
    referencesSection.id = `references-${timestamp}`;
    referencesSection.className = 'references-section hidden';
    
    const footerNote = document.createElement('div');
    footerNote.id = `footer-note-${timestamp}`;
    footerNote.className = 'hidden';
    
    responseBodyWrapper.appendChild(output);
    responseBodyWrapper.appendChild(referencesSection);
    responseBodyWrapper.appendChild(footerNote);
    searchResultWrapper.appendChild(responseBodyWrapper);
    
    // Create toolbar with unique ID
    const toolbar = document.createElement('div');
    toolbar.id = `toolbar-${timestamp}`;
    toolbar.className = 'hidden';
    toolbar.innerHTML = `
        <button class="icon-btn" id="thumbsup-${timestamp}" onclick="logResponse('Positive', '${timestamp}')" data-tooltip="Good Response" data-short-label="Good Response"><span class="material-symbols-rounded notranslate">thumb_up</span></button>
        <button class="icon-btn" id="thumbsdown-${timestamp}" onclick="logResponse('Negative', '${timestamp}')" data-tooltip="Bad Response" data-short-label="Bad Response"><span class="material-symbols-rounded notranslate">thumb_down</span></button>
        <button class="icon-btn" id="copybtn-${timestamp}" onclick="copyText(this)" data-tooltip="Copy Response" data-short-label="Copy Response"><span class="material-symbols-rounded notranslate">content_copy</span></button>
        <button class="icon-btn" id="downloadbtn-${timestamp}" onclick="download(this)" data-tooltip="Download PDF" data-short-label="Download PDF"><span class="material-symbols-rounded notranslate">download</span></button>
        <button class="icon-btn" id="pubmedbtn-${timestamp}" data-tooltip="Reproducible PubMed Results (1-click)" data-short-label="PubMed Results"><span class="material-symbols-rounded notranslate">quick_reference_all</span></button>
        <button class="icon-btn" id="comparebtn-${timestamp}" data-tooltip="Summary Table — compare studies side-by-side" data-short-label="Summary Table"><span class="material-symbols-rounded notranslate">table_view</span></button>
    `;
    const pubmedBtn = toolbar.querySelector(`#pubmedbtn-${timestamp}`);
    if (pubmedBtn) {
        toolbar.insertBefore(createCitationFormatDropdown(timestamp), pubmedBtn);
    }
    searchResultWrapper.appendChild(toolbar);
    
    // Append the entire search result wrapper to container
    container.appendChild(searchResultWrapper);
    
    // Scroll to the new search-result-wrapper after appending the spinner
    requestAnimationFrame(() => {
        searchResultWrapper.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
        });
    });

    // Step 1: Analyze the query and get the refined query (skip only if from suggested chip).
    // Runs after the spinner is already visible — it only refines what to search for, it
    // doesn't gate whether to search, so there's no reason to make the user wait on it first.
    if (!skipAnalysis && isFirstQuery) {
        await clearActiveResearchIdOnServer();
        setCurrentResearchId(null);
        currentResearchId = null;
    }

    console.log('Search info:', { skipAnalysis, isFirstQuery, hasSearched: sessionStorage.getItem('bms_has_searched') });

    // Always call analyze-query to initialize/maintain chat history (skip only if from suggested chip)
    if (!skipAnalysis) {
        const analyzeParams = new URLSearchParams({
            query: query,
            sessionId: sessionIdForSearch
        });
        currentResearchId = getCurrentResearchId();
        if (currentResearchId) {
            analyzeParams.append('research_id', currentResearchId);
        }
        const analyzeUrl = `/analyze-query?${analyzeParams.toString()}`;

        console.log('Calling analyze-query with:', { query, sessionId: sessionIdForSearch });
        const authHeaders = getAuthHeaders();
        console.log('[Auth] analyze-query request headers:', Object.keys(authHeaders).length > 0 ? 'Authorization header present' : 'No auth headers');

        const analyzeResponse = await authenticatedFetch(analyzeUrl, {
            headers: authHeaders
        });
        const analyzeData = await analyzeResponse.json();
        if (analyzeResponse.ok) {
            refinedQuery = analyzeData.refinedQuery;
            if (analyzeData.research_id) {
                console.log('[Research] analyze-query resolved research_id:', analyzeData.research_id);
                setCurrentResearchId(analyzeData.research_id);
                currentResearchId = analyzeData.research_id;
            }
        } else {
            console.warn('[Research] analyze-query failed:', analyzeData);
        }

        console.log('Received refined query:', refinedQuery);
    } else {
        console.log('Skipping query refinement (from suggested chip or edited query)');
    }

    // Mark that a search has been performed
    markSearchPerformed();

    // Step 2: Perform the search with the refined query
    // Get pubTypes from filter modal
    let pubTypesParam = '';
    if (window.getPubTypeValues) {
        const pubTypes = window.getPubTypeValues();
        if (pubTypes && pubTypes.length > 0) {
            pubTypesParam = `&pubTypes=${encodeURIComponent(pubTypes.join(','))}`;
        }
    }
    
    let pubYearParam = '';
    if (window.getPubYearRange) {
        const yearRange = window.getPubYearRange();
        console.log('[Filter] Year range from getPubYearRange:', yearRange);
        if (yearRange) {
            if (typeof yearRange.start === 'number') {
                pubYearParam += `&pubStartDate=${encodeURIComponent(yearRange.start)}`;
            }
            if (typeof yearRange.end === 'number') {
                pubYearParam += `&pubEndDate=${encodeURIComponent(yearRange.end)}`;
            }
        }
    }
    console.log('[Filter] Year parameters string:', pubYearParam);
    
    const searchAuthHeaders = getAuthHeaders();
    console.log('[Auth] /search request headers:', Object.keys(searchAuthHeaders).length > 0 ? 'Authorization header present' : 'No auth headers');
    
    // Add research_id to search URL if available
    // Note: If not available, backend will auto-create it for authenticated users
    let researchIdParam = '';
    currentResearchId = getCurrentResearchId();
    if (currentResearchId) {
        researchIdParam = `&research_id=${encodeURIComponent(currentResearchId)}`;
        console.log('[Research] Using existing research_id:', currentResearchId);
    } else {
        console.log('[Research] No currentResearchId - backend will create one if user is authenticated');
    }
    // Pass sessionId so backend can save user_query_context when results > 0 (same id used for analyze-query)
    // Use getOrCreateSessionId() again here so we always send it even if scope/cache issues; same value from sessionStorage
    const sessionIdForSearchUrl = getOrCreateSessionId();
    let sessionIdParam = '';
    if (sessionIdForSearchUrl) {
        sessionIdParam = `&sessionId=${encodeURIComponent(sessionIdForSearchUrl)}`;
    }
    // Log the complete URL for debugging
    // Pass both original query and refinedQuery to backend
    const searchUrl = `/search?query=${encodeURIComponent(query)}&refinedQuery=${encodeURIComponent(refinedQuery)}${pubTypesParam}${pubYearParam}${researchIdParam}${sessionIdParam}`;
    console.log('[Research] Search URL:', searchUrl);

    await authenticatedFetch(searchUrl, {
        headers: searchAuthHeaders
    }).then(async (response) => {
        if (response.status === 403) {
            const err = await response.json().catch(() => ({}));
            if (err.detail === 'fair_use_limit') {
                fairUseLimitHit = true;
                applySearchLimitReached(true, {});
            }
            searchInProgress = false;
            return;
        }
        const reader = response.body.getReader();
        const headers = response.headers;
        const docCount = headers.get('DocCount');
        // Update "Free plan : X searches remaining" from response (count after this search)
        const searchCountHeader = headers.get('SearchCount') || headers.get('searchcount');
        const expiresAtHeader = headers.get('Expires-At') || headers.get('expires-at');
        const fairUseThrottleHeader = headers.get('Fair-Use-Throttle') || headers.get('fair-use-throttle');
        if (fairUseThrottleHeader === 'true') {
            updateFairUseThresholdMessage(true, false);
        }
        if (searchCountHeader) {
            const newCount = parseInt(searchCountHeader, 10);
            if (!isNaN(newCount)) {
                searchCountAfterSearch = newCount;
                if (isPro) {
                    updateFreePlanRemainingMessage(null);
                } else {
                    const expiresAt = expiresAtHeader ? parseInt(expiresAtHeader, 10) : undefined;
                    updateFreePlanRemainingMessage(newCount, !isNaN(expiresAt) ? expiresAt : undefined);
                }
            }
        }
        // Try multiple header name variations (HTTP headers are case-insensitive)
        const searchId = headers.get('SearchID') || headers.get('searchid') || headers.get('search-id') || headers.get('Search-Id');
        // Read ResearchID from headers (created automatically by backend if user is authenticated)
        const researchIdFromHeaders = headers.get('ResearchID') || headers.get('researchid') || headers.get('research-id') || headers.get('Research-Id');
        // Read Citations from headers (base64 encoded JSON)
        const citationsHeader = headers.get('Citations') || headers.get('citations') || headers.get('citations-header');
        console.log('[Research] Attempting to read ResearchID from headers:', {
            'ResearchID': headers.get('ResearchID'),
            'researchid': headers.get('researchid'),
            'research-id': headers.get('research-id'),
            'Research-Id': headers.get('Research-Id'),
            'found': researchIdFromHeaders,
            'currentResearchId_before': currentResearchId
        });
        if (researchIdFromHeaders) {
            if (currentResearchId !== researchIdFromHeaders) {
                console.log('[Research] Updating research_id:', currentResearchId, '->', researchIdFromHeaders);
            }
            setCurrentResearchId(researchIdFromHeaders);
            currentResearchId = researchIdFromHeaders;
        } else {
            console.warn('[Research] No ResearchID found in response headers. This might happen if user is not authenticated or backend did not create a research session.');
        }
        // Log all headers to see what's available
        const allHeadersArray = Array.from(headers.entries());
        console.log('[Research] All response headers:', allHeadersArray);
        console.log('[Research] Response headers summary:', { 
            docCount, 
            searchId,
            researchId: researchIdFromHeaders,
            currentResearchId: currentResearchId,
            allHeaderNames: allHeadersArray.map(([name, value]) => name),
            headerCount: allHeadersArray.length
        });
        const compactFormatter = new Intl.NumberFormat('en-US');
        posthogCapture('search_performed', {
            query_length: query.length,
            skip_analysis: !!skipAnalysis,
            is_first_query: isFirstQuery,
            doc_count: docCount ? parseInt(docCount, 10) : 0
        });
        const pubmedUrlB64 = headers.get('PubmedUrl');
        const directPubmedUrl = pubmedUrlB64 ? atob(pubmedUrlB64) : null;

        const pubmedBtn = document.getElementById(`pubmedbtn-${timestamp}`);
        if (pubmedBtn) {
            pubmedBtn.setAttribute('data-search-id', searchId);
            if (directPubmedUrl) pubmedBtn.setAttribute('data-direct-url', directPubmedUrl);
            pubmedBtn.addEventListener('click', () => {
                const directUrl = pubmedBtn.getAttribute('data-direct-url');
                if (directUrl) {
                    window.open(directUrl, '_blank');
                } else {
                    const currentResearchId = getCurrentResearchId();
                    goToPubMed(searchId, currentResearchId);
                }
            });
        }
        
        if (docCount && parseInt(docCount) > 0) {
            const footerNoteEl = document.getElementById(`footer-note-${timestamp}`);
            if (footerNoteEl) {
                const searchDateStr = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                footerNoteEl.innerHTML = `<p class="footer-note-line"><small><span class="footer-note-count">${compactFormatter.format(docCount)} results found in PubMed</span></small><small class="footer-note-date">${searchDateStr}</small></p>`;
            }
        } else {
            const footerNoteEl = document.getElementById(`footer-note-${timestamp}`);
            if (footerNoteEl) {
                footerNoteEl.innerHTML = '';
            }
        }
        const decoder = new TextDecoder();
        output.innerHTML = '';
        output[BMS_CANONICAL_INNER_HTML_KEY] = '';
        let buffer = '';
        let tableStr = '';
        let responseHtmlUpdated = false; // Flag to prevent multiple updates

        const appendStreamToOutput = (msg) => {
            const added = updateAssistantMessage(output, msg);
            if (added) {
                output[BMS_CANONICAL_INNER_HTML_KEY] =
                    (output[BMS_CANONICAL_INNER_HTML_KEY] || '') + added;
            }
        };

        while (true) {
            const { done, value } = await reader.read();
            if (done) {
                // Show toolbar only if docCount > 0
                const hasResults = docCount && parseInt(docCount) > 0;
                
                if (hasResults) {
                    showTools(timestamp);
                } else {
                    // Hide toolbar if no results (docCount is 0 or missing)
                    const toolbar = document.getElementById(`toolbar-${timestamp}`);
                    if (toolbar) {
                        toolbar.classList.remove('visible');
                        toolbar.classList.add('hidden');
                    }
                }
                
                const footerNote = document.getElementById(`footer-note-${timestamp}`);
                footerNote.classList.add('visible');
                
                // Create compare button if SearchID is available
                if (searchId) {
                    // Store searchId in data attribute on search result wrapper and buttons for later retrieval
                    const searchResultWrapper = document.getElementById(`search-result-wrapper-${timestamp}`);
                    if (searchResultWrapper) {
                        searchResultWrapper.setAttribute('data-search-id', searchId);
                    }
                    const responseBodyWrapper = document.getElementById(`response-body-wrapper-${timestamp}`);
                    if (responseBodyWrapper) {
                        responseBodyWrapper.setAttribute('data-search-id', searchId);
                    }
                    const compareBtn = document.getElementById(`comparebtn-${timestamp}`);
                    if (compareBtn) {
                        compareBtn.setAttribute('data-search-id', searchId);
                        const citationsB64 = headers.get('Citations');
                        if (citationsB64) compareBtn.setAttribute('data-citations-b64', citationsB64);
                    }
                    createCompareButton(timestamp, searchId);
                } else {
                    console.log('SearchID not available in response headers');
                }
                
                // Function to update response_html after all async operations complete
                const updateResponseHtml = () => {
                    const currentResearchId = getCurrentResearchId();
                    console.log('[Research] updateResponseHtml called', {
                        responseHtmlUpdated,
                        searchId,
                        currentResearchId,
                        timestamp
                    });
                    
                    if (responseHtmlUpdated) {
                        console.log('[Research] Already updated, skipping');
                        return; // Already updated, skip
                    }
                    
                    if (!searchId) {
                        console.warn('[Research] searchId not available, cannot update response_html');
                        return;
                    }
                    
                    if (!currentResearchId) {
                        console.warn('[Research] currentResearchId not available, cannot update response_html');
                        return;
                    }
                    
                    responseHtmlUpdated = true; // Set flag to prevent multiple updates
                    // Use requestAnimationFrame and delay to ensure DOM is fully updated
                    requestAnimationFrame(() => {
                        setTimeout(() => {
                            // Get the output element HTML (response HTML)
                            const outputElement = document.getElementById(`output-${timestamp}`);
                            if (outputElement) {
                                const responseHtml = getResearchPersistedOutputHtml(timestamp, outputElement);
                                const htmlLength = responseHtml ? responseHtml.length : 0;
                                console.log('[Research] Capturing response_html for search_id:', searchId, 'research_id:', currentResearchId, 'length:', htmlLength);
                                
                                if (!responseHtml || htmlLength === 0) {
                                    console.warn('[Research] response_html is empty, skipping update');
                                    responseHtmlUpdated = false; // Reset flag to allow retry
                                    return;
                                }
                                
                                // Call backend to update response_html
                                const authHeaders = getAuthHeaders();
                                console.log('[Research] Sending update request with auth headers:', Object.keys(authHeaders).length > 0 ? 'Authorization header present' : 'No auth headers');
                                
                                authenticatedFetch('/update-search-response', {
                                    method: 'POST',
                                    headers: {
                                        'Content-Type': 'application/json',
                                        ...authHeaders
                                    },
                                    body: JSON.stringify({
                                        research_id: currentResearchId,
                                        search_id: searchId,
                                        response_html: responseHtml
                                    })
                                }).then(response => {
                                    if (response.ok) {
                                        return response.json().then(data => {
                                            console.log('[Research] Successfully updated response_html for search_id:', searchId, 'response:', data);
                                        });
                                    } else {
                                        console.warn('[Research] Failed to update response_html:', response.status);
                                        responseHtmlUpdated = false; // Reset flag on error to allow retry
                                        return response.json().then(data => {
                                            console.warn('[Research] Error details:', data);
                                        }).catch(err => {
                                            console.warn('[Research] Could not parse error response:', err);
                                        });
                                    }
                                }).catch(error => {
                                    console.error('[Research] Error updating response_html:', error);
                                    responseHtmlUpdated = false; // Reset flag on error to allow retry
                                });
                            } else {
                                console.warn('[Research] Output element not found for timestamp:', timestamp, 'element ID:', `output-${timestamp}`);
                                // Try to find any element with output- prefix to debug
                                const allOutputs = document.querySelectorAll('[id^="output-"]');
                                console.warn('[Research] Found output elements:', Array.from(allOutputs).map(el => el.id));
                                responseHtmlUpdated = false; // Reset flag if element not found
                            }
                        }, 500); // Short delay for DOM to settle; save-before-navigate in New Research button handles early clicks
                    });
                };
                
                // Display citations from headers or fetch from API
                // Wait for citations to complete before updating HTML
                let citationsPromise = Promise.resolve();
                if (citationsHeader) {
                    // Decode and display citations from response headers
                    try {
                        const citationsJson = atob(citationsHeader);
                        const citations = JSON.parse(citationsJson);
                        displayCitations(timestamp, citations);
                        console.log('[Citations] Displayed citations from headers');
                    } catch (error) {
                        console.error('[Citations] Error decoding citations from headers:', error);
                        // Fallback to API fetch if header decoding fails
                        if (searchId) {
                            const currentResearchId = getCurrentResearchId();
                            if (currentResearchId) {
                                citationsPromise = fetchAndDisplayCitations(timestamp, searchId, currentResearchId);
                            }
                        }
                    }
                } else if (searchId) {
                    const currentResearchId = getCurrentResearchId();
                    if (currentResearchId) {
                        // Fallback: fetch citations from API if not in headers
                        citationsPromise = fetchAndDisplayCitations(timestamp, searchId, currentResearchId);
                    }
                }
                
                // Wait for citations, then fetch suggestions, then update HTML
                citationsPromise.then(() => {
                    console.log('[Research] Citations loaded, fetching suggestions');
                    // Fetch and display suggested chips using the refined query
                    // Note: suggested chips will be appended to searchResultWrapper
                    return fetchSuggestions(refinedQuery, timestamp, searchResultWrapper);
                }).then(() => {
                    console.log('[Research] Suggestions loaded, updating response_html');
                    // Wait for the "New Search" button to be displayed before saving HTML
                    // The new search button is created inside createSuggestedChips
                    waitForNewSearchButtonAndSave(timestamp);
                    // Update response_html after suggestions are loaded
                    updateResponseHtml();
                }).catch(error => {
                    console.error('[Research] Error in async operations, still attempting to update HTML:', error);
                    // Still try to save HTML even if citations or suggestions fail
                    // Wait a bit for DOM to settle, then check for button
                    waitForNewSearchButtonAndSave(timestamp);
                    // Update response_html even if operations fail
                    updateResponseHtml();
                });

                break;
            }
            const chunk = decoder.decode(value);
            buffer += chunk;

            // Process the buffer for SSE messages
            let lines = buffer.split('\n');

            // Process all complete lines except possibly incomplete last line
            for (let i = 0; i < lines.length - 1; i++) {
                const line = lines[i].trim();
                if (line.includes("|")) {
                    tableStr += '\n'+line;
                    continue;
                } else if (tableStr.length > 0) {
                    // Process the accumulated table string
                    appendStreamToOutput(tableStr);
                    tableStr = '';
                }
                appendStreamToOutput(line);
            }
            // Keep the last line in buffer (in case it's incomplete)
            buffer = lines[lines.length - 1];
            }
            // Process any remaining buffer content
            if (buffer.trim()) {
                appendStreamToOutput(buffer);
            }
    }).catch((err) => {
        console.error('[handleSearch] Search request failed:', err);
    });
    } finally {
        searchInProgress = false;
        // Don't re-enable if Pro hit fair-use limit (1000)
        if (fairUseLimitHit) return;
        // Pro users: always re-enable. Free users: only re-enable if under limit
        if (isPro || searchCountAfterSearch == null || searchCountAfterSearch < FREE_SEARCH_LIMIT) {
            enableSearchUI();
        }
    }
}
